import React, { useState, useMemo } from "react";
import { useFormik } from "formik";
import { Box, Button, Grid, TextField, Typography, Divider, Tabs, Tab } from "@mui/material";
import { useProductFormValidationSchema } from "./validation";
import { useTranslation } from "react-i18next";

const DEFAULT_LANGUAGES = ["en-GB", "de-DE", "it-IT", "de-IT"];

const LANG_FIELDS = ["name", "description", "shortDescription", "validityText", "soldText", "urlPath"];

const ProductForm = ({
  handleSubmit,
  isLoading,
  buttonText,
  productVariants,
  mode,
}) => {
  const { t } = useTranslation();
  const validationSchema = useProductFormValidationSchema();
  const [langTab, setLangTab] = useState(0);

  const languages = useMemo(() => {
    if (productVariants && productVariants.length > 0) {
      return productVariants.map((v) => v.language);
    }
    return DEFAULT_LANGUAGES;
  }, [productVariants]);

  const firstVariant = productVariants?.[0] || {};

  const buildLangInitialValues = () => {
    const langValues = {};
    languages.forEach((lang) => {
      const variant = productVariants?.find((v) => v.language === lang);
      langValues[lang] = {};
      LANG_FIELDS.forEach((field) => {
        langValues[lang][field] = variant?.[field] || "";
      });
    });
    return langValues;
  };

  const formik = useFormik({
    initialValues: {
      sku: firstVariant.sku || "",
      price: firstVariant.price ?? "",
      priceNet: firstVariant.priceNet ?? "",
      currency: firstVariant.currency || "EUR",
      maxSaleQty: firstVariant.maxSaleQty ?? "",
      taxClass: firstVariant.taxClass || "",
      taxClassCode: firstVariant.taxClassCode ?? "",
      ticketValidityAmountPeople: firstVariant.ticketValidityAmountPeople || "",
      productScheduleStartDate: firstVariant.productScheduleStartDate || "",
      productScheduleEndDate: firstVariant.productScheduleEndDate || "",
      productValidityType: firstVariant.productValidityType || "",
      productValidity: firstVariant.productValidity || "",
      validityRangeStart: firstVariant.validityRangeStart || "",
      validityRangeEnd: firstVariant.validityRangeEnd || "",
      productDurationType: firstVariant.productDurationType || "",
      duration: firstVariant.duration ?? "",
      productArrivalOffset: firstVariant.productArrivalOffset || "",
      productAutoActivation: firstVariant.productAutoActivation || "",
      productAutoActivationType: firstVariant.productAutoActivationType || "",
      productAutoActivationBefore: firstVariant.productAutoActivationBefore || "",
      productAutoActivationAfter: firstVariant.productAutoActivationAfter || "",
      productUserActivation: firstVariant.productUserActivation || "",
      productUserActivationType: firstVariant.productUserActivationType || "",
      productUserActivationBefore: firstVariant.productUserActivationBefore || "",
      productUserActivationAfter: firstVariant.productUserActivationAfter || "",
      productUserRefund: firstVariant.productUserRefund || "",
      productUserRefundType: firstVariant.productUserRefundType || "",
      productUserRefundAfter: firstVariant.productUserRefundAfter || "",
      productUserRefundBefore: firstVariant.productUserRefundBefore || "",
      langValues: buildLangInitialValues(),
    },
    validationSchema,
    onSubmit: (values) => {
      const { langValues, ...shared } = values;
      handleSubmit({ shared, langValues, productVariants });
    },
    enableReinitialize: true,
  });

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const currentLang = languages[langTab] || languages[0];

  return (
    <Box pb={2} px={2} sx={{ maxHeight: "70vh", overflowY: "auto" }}>
      <form onSubmit={formik.handleSubmit}>
        {/* Basic Info */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
          {t("product.basicInfo")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="sku"
              label={t("product.sku")}
              value={formik.values.sku}
              onChange={mode === "add" ? formik.handleChange : undefined}
              disabled={mode === "edit"}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Pricing */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.pricing")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              name="price"
              label={`${t("product.price")} *`}
              type="number"
              value={formik.values.price}
              onChange={formik.handleChange}
              error={formik.touched.price && Boolean(formik.errors.price)}
              helperText={formik.touched.price && formik.errors.price}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              name="priceNet"
              label={t("product.priceNet")}
              type="number"
              value={formik.values.priceNet}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              name="currency"
              label={t("product.currency")}
              value={formik.values.currency}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              name="maxSaleQty"
              label={t("product.maxSaleQty")}
              type="number"
              value={formik.values.maxSaleQty}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="taxClass"
              label={t("product.taxClass")}
              value={formik.values.taxClass}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="taxClassCode"
              label={t("product.taxClassCode")}
              type="number"
              value={formik.values.taxClassCode}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Schedule */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.schedule")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productScheduleStartDate"
              label={t("product.productScheduleStartDate")}
              type="datetime-local"
              value={formatDateForInput(formik.values.productScheduleStartDate)}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productScheduleEndDate"
              label={t("product.productScheduleEndDate")}
              type="datetime-local"
              value={formatDateForInput(formik.values.productScheduleEndDate)}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Validity */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.validity")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="productValidityType"
              label={t("product.productValidityType")}
              value={formik.values.productValidityType}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="productValidity"
              label={t("product.productValidity")}
              value={formik.values.productValidity}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="ticketValidityAmountPeople"
              label={t("product.ticketValidityAmountPeople")}
              value={formik.values.ticketValidityAmountPeople}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="validityRangeStart"
              label={t("product.validityRangeStart")}
              value={formik.values.validityRangeStart || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="validityRangeEnd"
              label={t("product.validityRangeEnd")}
              value={formik.values.validityRangeEnd || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="productDurationType"
              label={t("product.productDurationType")}
              value={formik.values.productDurationType}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="duration"
              label={t("product.duration")}
              type="number"
              value={formik.values.duration}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="productArrivalOffset"
              label={t("product.productArrivalOffset")}
              value={formik.values.productArrivalOffset || ""}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Auto Activation */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.autoActivation")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productAutoActivation"
              label={t("product.productAutoActivation")}
              value={formik.values.productAutoActivation || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productAutoActivationType"
              label={t("product.productAutoActivationType")}
              value={formik.values.productAutoActivationType || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productAutoActivationBefore"
              label={t("product.productAutoActivationBefore")}
              value={formik.values.productAutoActivationBefore || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productAutoActivationAfter"
              label={t("product.productAutoActivationAfter")}
              value={formik.values.productAutoActivationAfter || ""}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* User Activation */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.userActivation")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserActivation"
              label={t("product.productUserActivation")}
              value={formik.values.productUserActivation || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserActivationType"
              label={t("product.productUserActivationType")}
              value={formik.values.productUserActivationType || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserActivationBefore"
              label={t("product.productUserActivationBefore")}
              value={formik.values.productUserActivationBefore || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserActivationAfter"
              label={t("product.productUserActivationAfter")}
              value={formik.values.productUserActivationAfter || ""}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* User Refund */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("product.userRefund")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserRefund"
              label={t("product.productUserRefund")}
              value={formik.values.productUserRefund || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserRefundType"
              label={t("product.productUserRefundType")}
              value={formik.values.productUserRefundType || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserRefundBefore"
              label={t("product.productUserRefundBefore")}
              value={formik.values.productUserRefundBefore || ""}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              name="productUserRefundAfter"
              label={t("product.productUserRefundAfter")}
              value={formik.values.productUserRefundAfter || ""}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Language Tabs */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("LANGUAGE")}
        </Typography>
        <Tabs
          value={langTab}
          onChange={(_, newVal) => setLangTab(newVal)}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {languages.map((lang) => (
            <Tab key={lang} label={lang} />
          ))}
        </Tabs>
        {currentLang && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.name`}
                label={`${t("product.name")} (${currentLang}) *`}
                value={formik.values.langValues[currentLang]?.name || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.shortDescription`}
                label={`${t("product.shortDescription")} (${currentLang})`}
                value={formik.values.langValues[currentLang]?.shortDescription || ""}
                onChange={formik.handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.description`}
                label={`${t("product.description")} (${currentLang})`}
                value={formik.values.langValues[currentLang]?.description || ""}
                onChange={formik.handleChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.validityText`}
                label={`${t("product.validityText")} (${currentLang})`}
                value={formik.values.langValues[currentLang]?.validityText || ""}
                onChange={formik.handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.soldText`}
                label={`${t("product.soldText")} (${currentLang})`}
                value={formik.values.langValues[currentLang]?.soldText || ""}
                onChange={formik.handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name={`langValues.${currentLang}.urlPath`}
                label={`${t("product.urlPath")} (${currentLang})`}
                value={formik.values.langValues[currentLang]?.urlPath || ""}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        )}

        <Box py={2} />
        <Button
          color="secondary"
          variant="contained"
          fullWidth
          type="submit"
          disabled={isLoading}
        >
          {buttonText}
        </Button>
      </form>
    </Box>
  );
};

export default ProductForm;
